import type { SearchVideoOptions } from "@/models/search";
import type { Video } from "@/models/video";
import {
  API_BASE,
  type APIResponse,
  type ApiTokenResponse,
  callApi,
} from "@/services/api";
import { searchOptsToQueryString } from "@/util/SearchUtil";

// Cache of ongoing requests to prevent duplicate concurrent requests
const ongoingRequests: Map<string, Promise<APIResponse<Video[]>>> = new Map();

/**
 * Normalize a searchVideos result to a Video[]. On failure, searchVideos
 * resolves to the thrown error (which has no `data`), so callers must never
 * assume `resp.data` is an array — doing so let a failed /videos/search crash
 * the routine preview (relatedVideos became undefined, then `.length` threw).
 */
export function videosFromResponse(
  resp: APIResponse<Video[]> | undefined | null,
): Video[] {
  const data = (resp as APIResponse<Video[]> | null)?.data;
  return Array.isArray(data) ? data : [];
}

export async function searchVideos(
  opts?: SearchVideoOptions,
): Promise<APIResponse<Video[]>> {
  try {
    // Create a unique key based on the search parameters
    const queryString = searchOptsToQueryString(opts);
    const requestKey = `${API_BASE}/videos/search?${queryString}`;

    // Check if there's already an identical request in progress
    if (ongoingRequests.has(requestKey)) {
      // Return the existing promise to avoid duplicate requests
      return ongoingRequests.get(requestKey)!;
    }

    // Create a new request promise
    const requestPromise = (async () => {
      try {
        const resp = await callApi<Video[]>(requestKey);
        return resp;
      } catch (e: unknown) {
        return e as APIResponse<Video[]>;
      } finally {
        // Remove from ongoing requests when complete
        ongoingRequests.delete(requestKey);
      }
    })();

    // Store the promise in our cache
    ongoingRequests.set(requestKey, requestPromise);

    // Return the promise
    return requestPromise;
  } catch (e: unknown) {
    return e as APIResponse<Video[]>;
  }
}

export async function videoBySlug(slug: string): Promise<APIResponse<Video>> {
  try {
    const resp = await callApi<Video>(`${API_BASE}/videos/by-slug/${slug}`);
    return resp;
  } catch (e: unknown) {
    return e as APIResponse<Video>;
  }
}

export async function videoById(
  id: number | string,
): Promise<APIResponse<Video>> {
  try {
    const resp = await callApi<Video>(`${API_BASE}/videos/${id}`);
    return resp;
  } catch (e: unknown) {
    return e as APIResponse<Video>;
  }
}

export async function videoByShortcode(
  shortCode: string,
): Promise<APIResponse<Video>> {
  try {
    const resp = await callApi<Video>(`${API_BASE}/videos/info/${shortCode}`);
    return resp;
  } catch (e: unknown) {
    return e as APIResponse<Video>;
  }
}

export async function videoToken(
  playbackId: string | string[],
): Promise<ApiTokenResponse | ApiTokenResponse[]> {
  try {
    const opts = { playbackId };
    const resp = await callApi(`${API_BASE}/videos/token`, {
      method: "POST",
      body: opts,
    });
    // A sequence's tokens come back as a bare JSON array, which callApi
    // spreads into an object keyed "0", "1", ... Rebuild the real array so
    // callers can tell per-clip tokens from a single one with Array.isArray.
    if (Array.isArray(playbackId) && !Array.isArray(resp)) {
      return Object.keys(resp)
        .filter((key) => /^\d+$/.test(key))
        .map((key) => (resp as unknown as ApiTokenResponse[])[Number(key)]);
    }
    return resp;
  } catch (e: unknown) {
    return e as ApiTokenResponse | ApiTokenResponse[];
  }
}
